// misc_gamemodel entities: MD3 / MDC models placed in the map. ET draws the
// static ones on the client (cg_spawn.c SP_misc_gamemodel): origin, angles (or
// angle), modelscale / modelscale_vec. Models with the ANIMATE spawnflag (2)
// loop "frames" frames from "start" at "fps" (g_misc.c); the game has no
// scripts, so every gamemodel is drawn, the scripted ones in their spawn state.

import { entityNum, entityVec, type Entity } from './entities';
import type { Vec3 } from './math';

export interface GameModelDef {
  entityNum: number;
  model: string;
  origin: Vec3;
  angles: Vec3;
  /** Per axis scale (modelscale_vec, or modelscale on all three). */
  scale: Vec3;
  animate: { frames: number; start: number; fps: number; reverse: boolean } | null;
}

export function gameModelDefs(entities: Entity[]): GameModelDef[] {
  const out: GameModelDef[] = [];
  entities.forEach((e, i) => {
    if ((e.classname ?? '').toLowerCase() !== 'misc_gamemodel' || !e.model) return;
    const angles: Vec3 = e.angles ? entityVec(e, 'angles') : [0, entityNum(e, 'angle'), 0];
    const s = entityNum(e, 'modelscale', 1) || 1;
    const scale: Vec3 = e.modelscale_vec ? entityVec(e, 'modelscale_vec') : [s, s, s];
    const flags = entityNum(e, 'spawnflags');
    const frames = Math.trunc(entityNum(e, 'frames'));
    out.push({
      entityNum: i,
      model: e.model.replace(/\\/g, '/').toLowerCase(),
      origin: entityVec(e, 'origin'),
      angles,
      scale,
      animate: flags & 2 && frames > 0 ? { frames, start: Math.trunc(entityNum(e, 'start')), fps: entityNum(e, 'fps', 20) || 20, reverse: e.reverse !== undefined } : null,
    });
  });
  return out;
}

/** The frame an animated gamemodel shows at `time` (seconds) and how far towards the next it is. */
export function gameModelFrame(a: NonNullable<GameModelDef['animate']>, time: number): { frame: number; next: number; lerp: number } {
  const pos = time * a.fps;
  const i = Math.floor(pos);
  const step = (n: number) => {
    const k = ((n % a.frames) + a.frames) % a.frames;
    return a.start + (a.reverse ? a.frames - 1 - k : k);
  };
  return { frame: step(i), next: step(i + 1), lerp: pos - i };
}
